import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/common";
import ReservationsTab from "@/components/stock/ReservationsTab";
import AdjustmentsTab from "@/components/stock/AdjustmentsTab";
import TransfersTab from "@/components/stock/TransfersTab";
import CountsTab from "@/components/stock/CountsTab";
import QcTab from "@/components/stock/QcTab";
import UploadTab from "@/components/stock/UploadTab";
import LedgerTab from "@/components/stock/LedgerTab";

const ALL = ["Overview", "Control Tower", "Reservations", "Adjustments", "Transfers", "Counts", "QC Hold", "Upload", "Ledger"];
const ROLE_TABS = {
  admin: ALL,
  warehouse_manager: ALL,
  supervisor: ["Overview", "Control Tower", "Reservations", "Adjustments", "Transfers", "Counts", "Ledger"],
  worker: ["Overview", "Adjustments", "Transfers", "Counts"],
  sales: ["Overview", "Reservations"],
  accounts: ["Overview", "Control Tower", "Ledger"],
  production: ["Overview"],
  qc: ["Overview", "QC Hold", "Ledger"],
  dispatch: ["Overview", "Control Tower", "Reservations", "Transfers", "Ledger"],
};

const TOWER_CARDS = [
  ["total_skus", "Total SKUs"], ["physical", "Physical Stock"], ["available", "Available", "text-signal-green"],
  ["reserved", "Reserved", "text-signal-amber"], ["picked", "Picked", "text-signal-blue"],
  ["packed", "Packed / Dispatch Staging", "text-signal-blue"], ["qc_stock", "QC Hold"],
  ["quarantine", "Quarantine", "text-signal-red"],
  ["damaged", "Damaged", "text-signal-red"], ["rejected", "Rejected", "text-signal-red"],
  ["staging_unlocated", "Staging (No Location)", "text-signal-amber"], ["in_production", "In Production (WIP)"],
  ["low_stock", "Low Stock SKUs", "text-signal-amber"], ["out_of_stock", "Out of Stock", "text-signal-red"],
  ["negative_stock", "Negative Stock", "text-signal-red"], ["stock_without_location", "SKUs w/o Location", "text-signal-amber"],
  ["stock_variance", "Bin Variance SKUs", "text-signal-red"], ["open_adjustments", "Open Adjustments"],
  ["open_cycle_counts", "Open Cycle Counts"], ["open_putaway_tasks", "Open Putaway Tasks"],
  ["overdue_reservations", "Overdue Reservations", "text-signal-amber"], ["open_transfers", "Pending Transfers"], ["in_transit", "In Transit"],
  ["approval_route_errors", "Approval Route Errors", "text-signal-red"], ["unreconciled", "Unreconciled Stock", "text-signal-red"],
];

export default function StockReservation() {
  const { user } = useAuth();
  const tabs = ROLE_TABS[user?.role] || ["Overview"];
  const [tab, setTab] = useState(tabs[0]);
  const [stock, setStock] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [valuation, setValuation] = useState(null);
  const [stockError, setStockError] = useState(null);
  const [tower, setTower] = useState(null);
  const [search, setSearch] = useState("");
  const canSeeValue = ["admin", "warehouse_manager", "accounts"].includes(user?.role);

  const loadStock = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([api.get("/stock/overview-rows"), api.get("/stock/alerts")]);
      setStock(s.data);
      setAlerts(a.data);
      setStockError(null);
    } catch (e) {
      setStockError(e.response?.data?.detail || "Failed to load stock — check your connection and retry");
    }
    if (canSeeValue) {
      api.get("/stock/valuation").then((r) => setValuation(r.data)).catch(() => setValuation(null));
    }
    api.get("/stock/control-tower").then((r) => setTower(r.data)).catch(() => setTower(null));
  }, [canSeeValue]);

  useEffect(() => { loadStock(); }, [loadStock]);

  const filteredStock = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return stock;
    const fields = (s) => [s.sku, s.name, s.item_code, s.barcode, s.category, s.subcategory, s.product_type, s.hsn, s.status, ...(s.warehouses || [])];
    return stock.filter((s) => fields(s).some((f) => f && String(f).toLowerCase().includes(q)) || (q === "low stock" && s.min_stock && s.physical < s.min_stock) || (q === "out of stock" && s.physical <= 0));
  }, [stock, search]);
  const OVERVIEW_CARDS = [["total_skus", "Total SKUs"], ["physical", "Total Physical"], ["available", "Available", "text-signal-green"], ["reserved", "Reserved", "text-signal-amber"],
    ["staging_unlocated", "Unlocated / Staging", "text-signal-amber"], ["qc_stock", "QC Hold"], ["quarantine", "Quarantine", "text-signal-red"], ["damaged", "Damaged", "text-signal-red"],
    ["in_production", "WIP"], ["finished_goods", "Finished Goods"], ["in_transit", "In Transit"], ["low_stock", "Low Stock", "text-signal-amber"], ["out_of_stock", "Out of Stock", "text-signal-red"],
    ["stock_variance", "Stock Variance", "text-signal-red"], ["open_adjustments", "Pending Adjustments"], ["open_transfers", "Pending Transfers"], ["open_putaway_tasks", "Putaway Pending", "text-signal-amber"]];

  return (
    <div className="space-y-5" data-testid="stock-page">
      <SectionTitle>Stock & Reservations</SectionTitle>

      <div className="flex gap-1.5 flex-wrap border-b border-border pb-2" data-testid="stock-tabs">
        {tabs.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`stock-tab-${t.toLowerCase().replaceAll(" ", "-")}`}
            className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-widest transition-colors ${
              tab === t ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent"}`}>{t}</button>
        ))}
      </div>

      {tab === "Control Tower" && (
        <div data-testid="control-tower-tab">
          {!tower ? (
            <p className="text-sm text-muted-foreground">Loading control tower…</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2">
              {TOWER_CARDS.map(([key, label, cls]) => (
                <div key={key} className="bg-card border border-border rounded-lg p-3" data-testid={`tower-${key}`}>
                  <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
                  <p className={`metric text-lg font-extrabold mt-0.5 ${cls || ""}`}>{(tower[key] ?? 0).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "Overview" && (
        <div className="space-y-4" data-testid="stock-overview">
          {tower && (
            <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-9 gap-1.5" data-testid="overview-cards">
              {OVERVIEW_CARDS.map(([key, label, cls]) => (
                <div key={key} className="bg-card border border-border rounded-md px-2 py-1.5" data-testid={`overview-${key}`}>
                  <p className="text-[8px] font-mono uppercase tracking-widest text-muted-foreground truncate">{label}</p>
                  <p className={`metric text-base font-extrabold ${cls || ""}`}>{(tower[key] ?? 0).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
          {alerts.length > 0 && (
            <div className="bg-signal-red/10 border border-signal-red/40 rounded-lg p-3 text-sm space-y-1" data-testid="stock-alerts">
              {alerts.map((a, i) => (
                <p key={`${a.type || "low_stock"}-${a.sku || i}`} data-testid={`alert-${a.type || "low_stock"}-${a.sku || i}`}>
                  {a.type === "out_of_stock" && <span className="font-semibold text-signal-red">Out of stock: {a.sku} ({a.name})</span>}
                  {(a.type === "low_stock" || !a.type) && <span className="font-semibold text-signal-amber">Low stock: {a.sku} ({a.physical}/{a.min_stock})</span>}
                  {a.type === "over_reserved" && <span className="font-semibold text-signal-red">Reservation conflict: {a.sku} — free stock {a.free}</span>}
                  {a.type === "qc_hold_pending" && <span className="font-semibold text-signal-blue">QC hold pending: {a.sku} ({a.hold} pcs)</span>}
                  {a.type === "below_msp_pending" && <span className="font-semibold text-signal-red">{a.count} below-MSP / floor price line(s) awaiting Admin/MD approval</span>}
                  {a.type === "upload_pending_approval" && <span className="font-semibold text-signal-amber">{a.count} stock upload(s) pending approval</span>}
                  {a.type === "stock_without_location" && <span className="font-semibold text-signal-amber">Stock without location: {a.sku} — {a.unlocated} pcs in staging (complete putaway)</span>}
                  {a.type === "negative_stock" && <span className="font-semibold text-signal-red">Negative stock: {a.sku} ({a.physical} pcs)</span>}
                  {a.type === "overdue_reservation" && <span className="font-semibold text-signal-red">{a.count} overdue soft reservation(s) past expiry</span>}
                  {a.type === "adjustment_pending" && <span className="font-semibold text-signal-amber">{a.count} stock adjustment(s) pending approval</span>}
                </p>
              ))}
            </div>
          )}
          {valuation && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="valuation-cards">
              {[["Total Inventory Value", valuation.totals.inventory_value], ["Available Value", valuation.totals.available_value],
                ["Reserved Value", valuation.totals.reserved_value], ["Hold / Damaged / Rejected Value", valuation.totals.hold_value]].map(([label, v]) => (
                <div key={label} className="bg-card border border-border rounded-lg p-3" data-testid={`valuation-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
                  <p className="metric text-lg font-extrabold mt-0.5">₹{(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</p>
                </div>
              ))}
            </div>
          )}
          <div className="bg-card border border-border rounded-lg overflow-x-auto">
            <div className="flex items-center justify-between px-5 pt-4 flex-wrap gap-2">
              <h3 className="text-lg font-semibold uppercase tracking-tight">Stock by SKU — click a SKU to open its Control Center</h3>
              <div className="flex gap-1 items-center" data-testid="stock-export-buttons">
                <Input className="h-7 w-72 text-xs" placeholder="Search SKU, item name, code, barcode, category, HSN, warehouse, status…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="stock-search" />
                {["csv", "xlsx", "pdf"].map((f) => (
                  <Button key={f} size="sm" variant="outline" className="h-7 text-xs" data-testid={`export-${f}`}
                    onClick={() => window.open(`${API}/stock/export?format=${f}&mode=full`, "_blank")}>{f.toUpperCase()}</Button>
                ))}
                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="export-low-stock"
                  onClick={() => window.open(`${API}/stock/export?format=csv&mode=low_stock`, "_blank")}>Low Stock</Button>
                {canSeeValue && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="export-valuation"
                    onClick={() => window.open(`${API}/stock/export?format=xlsx&mode=valuation`, "_blank")}>Valuation</Button>
                )}
              </div>
            </div>
            <table className="w-full text-sm mt-2" data-testid="stock-table">
              <thead><tr className="border-b border-border text-left">
                {["SKU", "Item", "Category / HSN", "Physical Stock", "Staging", "Reserved", "Picked", "Packed", "QC Hold", "Quarantine", "Damaged", "Rejected", "Free Stock", ...(canSeeValue ? ["FIFO Avg Cost", "Inventory Value"] : [])].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>))}
              </tr></thead>
              <tbody>
                {filteredStock.map((s) => (
                  <tr key={s.sku} className="border-b border-border/50" data-testid={`stock-row-${s.sku}`}>
                    <td className="px-4 py-3 metric font-bold"><Link to={`/stock/sku/${encodeURIComponent(s.sku)}`} className="text-primary underline underline-offset-2" data-testid={`sku-link-${s.sku}`}>{s.sku}</Link>{s.status === "archived" && <span className="ml-1 text-[9px] font-mono text-signal-red uppercase">archived</span>}</td>
                    <td className="px-4 py-3 text-xs max-w-48 truncate" data-testid={`stock-name-${s.sku}`}>{s.name}</td>
                    <td className="px-4 py-3 text-[10px] font-mono text-muted-foreground">{[s.category, s.hsn].filter(Boolean).join(" / ") || "—"}</td>
                    <td className="px-4 py-3 metric">{s.physical.toLocaleString()}</td>
                    <td className="px-4 py-3 metric text-muted-foreground" data-testid={`stock-unlocated-${s.sku}`}>{(s.unlocated || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 metric text-signal-amber">{s.reserved.toLocaleString()}</td>
                    <td className="px-4 py-3 metric text-signal-blue" data-testid={`stock-picked-${s.sku}`}>{(s.picked || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 metric text-signal-blue" data-testid={`stock-packed-${s.sku}`}>{(s.packed || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 metric text-signal-red">{(s.hold || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 metric text-signal-red" data-testid={`stock-quarantine-${s.sku}`}>{(s.quarantine || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 metric text-signal-amber">{(s.damaged || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 metric text-signal-red">{(s.rejected || 0).toLocaleString()}</td>
                    <td className={`px-4 py-3 metric font-bold ${s.free < 0 ? "text-signal-red" : "text-signal-green"}`}>{s.free.toLocaleString()}</td>
                    {canSeeValue && (() => { const v = (valuation?.rows || []).find((r) => r.sku === s.sku); return (<>
                      <td className="px-4 py-3 metric text-xs">{v?.fifo_avg_cost != null ? `₹${v.fifo_avg_cost}` : "—"}</td>
                      <td className="px-4 py-3 metric text-xs font-bold">{v?.inventory_value != null ? `₹${v.inventory_value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—"}</td>
                    </>); })()}
                  </tr>
                ))}
                {stockError && (
                  <tr><td colSpan={13} className="px-4 py-8 text-center text-signal-red" data-testid="stock-error">{stockError} — <button className="underline" onClick={loadStock}>retry</button></td></tr>
                )}
                {!stockError && stock.length === 0 && <tr><td colSpan={13} className="px-4 py-8 text-center text-muted-foreground">No stock yet — use Upload or Adjustments to add opening stock</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "Reservations" && <ReservationsTab />}
      {tab === "Adjustments" && <AdjustmentsTab />}
      {tab === "Transfers" && <TransfersTab />}
      {tab === "Counts" && <CountsTab />}
      {tab === "QC Hold" && <QcTab onChanged={loadStock} />}
      {tab === "Upload" && <UploadTab onChanged={loadStock} />}
      {tab === "Ledger" && <LedgerTab />}
    </div>
  );
}
